import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface RouteErrorFallbackProps {
  routeName: string;
  resetError: () => void;
}

function RouteErrorFallback({ routeName, resetError }: RouteErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-4 text-center">
      <div className="flex flex-col items-center gap-3">
        <AlertTriangle className="h-12 w-12 text-orange-500" />
        <h2 className="text-xl font-bold text-foreground">Something went wrong</h2>
        <p className="text-muted-foreground text-sm max-w-sm">
          The <span className="font-medium text-foreground">{routeName}</span> page hit an unexpected
          error. The rest of the app is still running.
        </p>
      </div>
      <Button
        variant="outline"
        onClick={resetError}
        className="gap-2"
      >
        <RefreshCw className="h-4 w-4" />
        Try again
      </Button>
    </div>
  );
}

interface RouteErrorBoundaryProps {
  routeName: string;
  children: React.ReactNode;
}

export function RouteErrorBoundary({ routeName, children }: RouteErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag("route", routeName);
      }}
      fallback={({ resetError }) => (
        <RouteErrorFallback routeName={routeName} resetError={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
